import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export type MenuItem =
  | { kind?: 'item'; label: string; icon?: ReactNode; hint?: string; onSelect: () => void; disabled?: boolean; danger?: boolean; active?: boolean }
  | { kind: 'divider' }

interface Props {
  /** The button that opens the menu. */
  trigger: ReactNode
  items: MenuItem[]
  label: string
  className?: string
  /** Align the menu's right edge to the button's right edge (for a "…" at the end of a row). */
  align?: 'left' | 'right'
}

/**
 * A small action menu under a button, portalled to <body> so no scroll
 * container clips it. Used for a day's "…" and status, and for a food tile
 * on touch screens, where hover affordances do not exist.
 */
export function Menu({ trigger, items, label, className = '', align = 'left' }: Props) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const r = btn.current?.getBoundingClientRect()
      const w = pop.current?.offsetWidth ?? 220
      const h = pop.current?.offsetHeight ?? 200
      if (!r) return
      let left = align === 'right' ? r.right - w : r.left
      left = Math.max(8, Math.min(left, window.innerWidth - w - 8))
      const below = r.bottom + 4
      const top = below + h <= window.innerHeight ? below : Math.max(8, r.top - 4 - h)
      setPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open, align])

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (!btn.current?.contains(t) && !pop.current?.contains(t)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation()
        setOpen(false)
        btn.current?.focus()
      }
    }
    // A scroll elsewhere moves the button out from under the popover, so close
    // rather than float; a scroll inside the popover is just the grid scrolling.
    const onScroll = (e: Event) => {
      if (pop.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={className}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        data-no-drag
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          <div
            ref={pop}
            className="menu-pop"
            role="menu"
            aria-label={label}
            style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
          >
            {items.map((it, i) =>
              it.kind === 'divider' ? (
                <div key={i} className="menu-divider" role="separator" />
              ) : (
                <button
                  key={i}
                  type="button"
                  role="menuitem"
                  className={`menu-item ${it.danger ? 'danger' : ''} ${it.active ? 'active' : ''}`}
                  disabled={it.disabled}
                  onClick={() => {
                    setOpen(false)
                    it.onSelect()
                  }}
                >
                  {it.icon !== undefined && (
                    <span className="menu-icon" aria-hidden="true">
                      {it.icon}
                    </span>
                  )}
                  <span className="menu-label">{it.label}</span>
                  {it.hint && <span className="menu-hint">{it.hint}</span>}
                </button>
              ),
            )}
          </div>,
          document.body,
        )}
    </>
  )
}
